import { bool, func, instanceOf, string } from 'prop-types'
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Plot from 'react-plotly.js'
import { Loader, Message } from 'semantic-ui-react'

export interface LineChartData {
  x: (number | Date)[]
  y: number[]
  mode: string
  line?: { color: string; width: number }
}

interface LinechartProps {
  data: LineChartData[]
  xaxis?: object
  yaxis?: object
  downloadFilename?: string
  uiRevisionKey?: string
  hasActiveSession?: boolean
  statusIndicators?: ReactNode
  variant?: 'default' | 'embed'
}

const UTC_TICK_FORMAT = '%H:%M:%S'
const COMPACT_EMBED_MAX_WIDTH = 480
const COMPACT_EMBED_MAX_HEIGHT = 360
// Long enough to survive a pause mid-drag, short enough not to strand a live feed
const DRAG_RESUME_TIMEOUT = 5000
const EMBED_MODEBAR_BUTTONS = [['toImage']]

const formatToUtcIso = (value: any) => {
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toISOString()
}

const buildUtcAxis = (axis: any = {}) => ({
  type: 'date',
  tickformat: UTC_TICK_FORMAT,
  hoverformat: UTC_TICK_FORMAT,
  ...axis,
})

const toTime = (value: any) => (value instanceof Date ? value.getTime() : new Date(value).getTime())

const prepareTraces = (traces: any[]) =>
  traces.map((trace: any) => ({
    ...trace,
    x: Array.isArray(trace?.x) ? trace.x.map(formatToUtcIso) : trace?.x,
  }))

// Access Plotly from window (loaded by react-plotly.js)
const getPlotly = () => (window as any).Plotly

// Cleanup for effects that bail out before subscribing to anything
const noCleanup = () => {}

function Linechart({
  data,
  xaxis,
  yaxis,
  downloadFilename,
  uiRevisionKey,
  hasActiveSession = true,
  statusIndicators,
  variant = 'default',
}: LinechartProps) {
  const [localXaxis, setLocalXaxis] = useState<any>()
  const [localYaxis, setLocalYaxis] = useState<any>()
  const [localDragmode, setLocalDragmode] = useState<string>('pan')
  const [initialized, setInitialized] = useState(false)
  const [isCompactEmbed, setIsCompactEmbed] = useState(
    () =>
      variant === 'embed' &&
      (window.innerWidth <= COMPACT_EMBED_MAX_WIDTH || window.innerHeight <= COMPACT_EMBED_MAX_HEIGHT),
  )
  const uiRevision = useMemo(() => uiRevisionKey || 'linechart', [uiRevisionKey])

  // Ref to access Plotly DOM element for extendTraces
  const plotRef = useRef<any>(null)
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const downloadFilenameRef = useRef(downloadFilename)

  // Track previous data to detect new points for streaming
  const prevDataRef = useRef<{ x: any[], y: any[] }>({ x: [], y: [] })

  // Initial data for first render only
  const [initialData, setInitialData] = useState<any>(null)

  // Track interaction for axis state management (not for data deferral)
  const isInteractingRef = useRef(false)
  const interactionEndTimerRef = useRef<any>(null)
  const isDraggingRef = useRef(false)
  const dragEndTimerRef = useRef<any>(null)
  const pendingResyncRef = useRef(false)

  // Reset state when uiRevisionKey changes (tab switch)
  useEffect(() => {
    setInitialData(null)
    prevDataRef.current = { x: [], y: [] }
    setLocalXaxis(undefined)
    setLocalYaxis(undefined)
    setLocalDragmode('pan')
    setInitialized(false)
  }, [uiRevisionKey])

  // Set initial data only once when first data arrives
  useEffect(() => {
    if (!initialData && data?.[0]?.x?.length > 0) {
      setInitialData(prepareTraces(data))
      prevDataRef.current = { x: [...data[0].x], y: [...data[0].y] }
    }
  }, [data, initialData])

  // Use extendTraces for streaming data updates - no re-render needed
  useEffect(() => {
    const el = plotRef.current?.el
    const Plotly = getPlotly()
    if (!el || !Plotly || !initialData || !data?.[0]?.x?.length) return

    // Guard: ensure the plot element has valid data attached before calling extendTraces
    if (!el.data || !Array.isArray(el.data) || el.data.length === 0) return

    // A redraw rebuilds Plotly's drag layer and kills an in-flight gesture, so on a
    // live feed a zoom box could never be finished. Tiles hold updates until the user
    // lets go, then resync in one go. The main chart keeps streaming as before.
    if (isEmbed && isDraggingRef.current) {
      pendingResyncRef.current = true
      return
    }

    const currentX = data[0].x
    const currentY = data[0].y
    const prevX = prevDataRef.current.x

    // Skip if this is the initial data (already rendered)
    if (prevX.length === 0) {
      prevDataRef.current = { x: [...currentX], y: [...currentY] }
      return
    }

    if (pendingResyncRef.current) {
      // Ticks missed during the gesture cannot be replayed one by one — the sliding
      // window would leave the trace out of step with the data. Redraw it whole.
      pendingResyncRef.current = false
      const prepared = prepareTraces(data)
      Plotly.react(el, prepared, el.layout)
      setInitialData(prepared)
      prevDataRef.current = { x: [...currentX], y: [...currentY] }
      return
    }

    const prevLastTime = toTime(prevX[prevX.length - 1])
    const currentFirstTime = toTime(currentX[0])
    const currentLastTime = toTime(currentX[currentX.length - 1])
    const appendedFrom = currentX.findIndex(value => toTime(value) > prevLastTime)
    const appendedCount = appendedFrom === -1 ? 0 : currentX.length - appendedFrom

    // A live window drops its oldest point on every tick, so a changed first point is
    // business as usual — testing that would treat every second as a new dataset. What
    // marks a continuation is overlapping with the previous series AND a length that
    // adds up; a swapped-in series covering the same span fails the second half.
    const continuesPrevious =
      Number.isFinite(prevLastTime) &&
      prevLastTime >= currentFirstTime &&
      prevLastTime <= currentLastTime &&
      (currentX.length === prevX.length || currentX.length === prevX.length + appendedCount)

    if (!continuesPrevious) {
      const prepared = prepareTraces(data)
      // Plotly.react for an immediate update without waiting on a React re-render
      Plotly.react(el, prepared, el.layout)
      setInitialData(prepared)
      prevDataRef.current = { x: [...currentX], y: [...currentY] }
      return
    }

    if (appendedCount > 0) {
      const appendedX = currentX.slice(appendedFrom).map(formatToUtcIso)
      const appendedY = currentY.slice(appendedFrom)

      // maxPoints trims the front so the window keeps its length
      Plotly.extendTraces(el, { x: [appendedX], y: [appendedY] }, [0], currentX.length)

      // Plotly re-renders from the `data` prop whenever layout or config identity
      // changes, so the prop has to track the trace. Mutating in place keeps its
      // identity — a fresh array would itself trigger the redraw we are avoiding.
      const trace = initialData[0]
      if (Array.isArray(trace?.x) && Array.isArray(trace?.y)) {
        trace.x.push(...appendedX)
        trace.y.push(...appendedY)
        const overflow = trace.x.length - currentX.length
        if (overflow > 0) {
          trace.x.splice(0, overflow)
          trace.y.splice(0, overflow)
        }
      }
    }

    prevDataRef.current = { x: [...currentX], y: [...currentY] }
  }, [data, initialData])

  const bumpInteraction = useCallback(() => {
    isInteractingRef.current = true
    if (interactionEndTimerRef.current) {
      clearTimeout(interactionEndTimerRef.current)
    }
    interactionEndTimerRef.current = setTimeout(() => {
      isInteractingRef.current = false
    }, 250)
  }, [])

  const beginDrag = useCallback(() => {
    isDraggingRef.current = true
    if (dragEndTimerRef.current) {
      clearTimeout(dragEndTimerRef.current)
    }
    // Safety net: a gesture that never reports its end must not freeze the feed
    dragEndTimerRef.current = setTimeout(() => {
      isDraggingRef.current = false
    }, DRAG_RESUME_TIMEOUT)
  }, [])

  const endDrag = useCallback(() => {
    isDraggingRef.current = false
    if (dragEndTimerRef.current) {
      clearTimeout(dragEndTimerRef.current)
      dragEndTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      if (interactionEndTimerRef.current) {
        clearTimeout(interactionEndTimerRef.current)
        interactionEndTimerRef.current = null
      }
      if (dragEndTimerRef.current) {
        clearTimeout(dragEndTimerRef.current)
        dragEndTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (variant !== 'embed') {
      setIsCompactEmbed(false)
      return noCleanup
    }

    const updateCompactMode = () => {
      setIsCompactEmbed(
        window.innerWidth <= COMPACT_EMBED_MAX_WIDTH || window.innerHeight <= COMPACT_EMBED_MAX_HEIGHT,
      )
    }

    updateCompactMode()
    window.addEventListener('resize', updateCompactMode)
    return () => window.removeEventListener('resize', updateCompactMode)
  }, [variant])

  useEffect(() => {
    if (!initialized) {
      setLocalXaxis(buildUtcAxis(xaxis))
      setLocalYaxis({ ...yaxis, rangemode: 'nonnegative' })
      setInitialized(true)
    } else {
      // Update axes when props change, but preserve user zoom/pan if set
      setLocalXaxis(prev => {
        const newAxis = buildUtcAxis(xaxis)
        if (prev?.range) {
          newAxis.range = prev.range
          newAxis.autorange = prev.autorange
        }
        return newAxis
      })
      setLocalYaxis(prev => {
        const newAxis: any = { ...yaxis, rangemode: 'nonnegative' }
        if (prev?.range) {
          newAxis.range = prev.range
          newAxis.autorange = prev.autorange
        }
        return newAxis
      })
    }
  }, [initialized, xaxis, yaxis])

  const isEmbed = variant === 'embed'
  const baseXaxis = localXaxis || buildUtcAxis(xaxis)
  const baseYaxis = localYaxis || { ...yaxis, rangemode: 'nonnegative' }

  const downloadPlot = useCallback(() => {
    const el = plotRef.current?.el
    const Plotly = getPlotly()
    if (!el || !Plotly?.downloadImage) return

    // scale:2 — tile-sized exports are unreadable at their native resolution
    Plotly.downloadImage(el, {
      format: 'png',
      filename: downloadFilename || 'plot',
      scale: 2,
    })
  }, [downloadFilename])

  // Plotly only emits relayouting once a drag clears its own movement threshold, so
  // the press itself has to arm the gate. A tick landing in between would redraw and
  // tear the gesture down before it ever started.
  useEffect(() => {
    const container = chartContainerRef.current
    if (!isEmbed || !container || !initialData) return noCleanup

    container.addEventListener('pointerdown', beginDrag)
    window.addEventListener('pointerup', endDrag)
    window.addEventListener('pointercancel', endDrag)

    return () => {
      container.removeEventListener('pointerdown', beginDrag)
      window.removeEventListener('pointerup', endDrag)
      window.removeEventListener('pointercancel', endDrag)
    }
  }, [beginDrag, endDrag, initialData, isEmbed])

  // The filename carries a timestamp, so it changes on every render. Plotly reads it
  // from _context at click time, which lets us keep it out of `config` entirely —
  // otherwise every passing second would hand react-plotly.js a new config object and
  // trigger a full redraw, wiping any gesture in flight.
  useEffect(() => {
    downloadFilenameRef.current = downloadFilename
    const options = plotRef.current?.el?._context?.toImageButtonOptions
    if (options) options.filename = downloadFilename || 'plot'
  }, [downloadFilename, initialData])

  // react-plotly.js compares data/layout/config by reference and redraws the whole
  // plot when any of them is new. An inline object here would redraw on every render —
  // once a second on a live feed — which defeats extendTraces and kills any drag in
  // flight. Identity has to stay stable.
  const config = useMemo(() => ({
    toImageButtonOptions: { filename: downloadFilenameRef.current || 'plot' },
    scrollZoom: !isEmbed,
    displayModeBar: isCompactEmbed ? false : ('hover' as const),
    // An explicit list beats add/remove here: it pins the embed toolbar to exactly
    // these buttons regardless of what Plotly ships by default.
    ...(isEmbed
      ? {
        modeBarButtons: EMBED_MODEBAR_BUTTONS,
        // Restores the full view from the current data, so a sliding window
        // cannot leave a stale range behind
        doubleClick: 'reset+autosize' as const,
      }
      : {
        modeBarButtonsToAdd: ['pan2d', 'autoScale2d', 'resetScale2d'],
        modeBarButtonsToRemove: ['zoom2d'],
      }),
    displaylogo: false,
  }), [isCompactEmbed, isEmbed])

  // Memoize layout to prevent unnecessary Plotly updates
  const layout = useMemo(() => {
    const embedXaxis = {
      ...baseXaxis,
      automargin: true,
      fixedrange: false,
      nticks: isCompactEmbed ? 3 : 5,
      tickfont: {
        ...baseXaxis.tickfont,
        size: isCompactEmbed ? 10 : 11,
      },
    }
    const embedYaxis = {
      ...baseYaxis,
      automargin: true,
      fixedrange: false,
      tickfont: {
        ...baseYaxis.tickfont,
        size: isCompactEmbed ? 10 : 11,
      },
    }
    const margin = isEmbed
      ? {
        l: isCompactEmbed ? 42 : 46,
        r: isCompactEmbed ? 8 : 16,
        b: isCompactEmbed ? 38 : 44,
        t: isCompactEmbed ? 48 : 36,
        pad: 2,
      }
      : {
        l: 50,
        r: 50,
        b: 50,
        t: 50,
        pad: 4,
      }

    return {
      autosize: true,
      showlegend: false,
      // Drag draws a zoom box: the plot never slides, but the reader can still pick a region
      dragmode: isEmbed ? 'zoom' : localDragmode,
      xaxis: isEmbed ? embedXaxis : baseXaxis,
      yaxis: isEmbed ? embedYaxis : baseYaxis,
      uirevision: uiRevision,
      margin,
    }
  }, [baseXaxis, baseYaxis, isCompactEmbed, isEmbed, localDragmode, uiRevision])

  const stateContainerStyle = {
    height: isEmbed ? '100%' : 500,
    minHeight: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }

  if (!data || !Array.isArray(data) || data.length === 0 || !data[0].x || data[0].x.length === 0) {
    if (!hasActiveSession) {
      return (
        <div style={stateContainerStyle}>
          <Message info>
            <Message.Header>No Active Observation Session</Message.Header>
            <p>There are currently no active observation sessions.</p>
          </Message>
        </div>
      )
    }

    return (
      <div style={stateContainerStyle}>
        <Loader active />
      </div>
    )
  }

  // Wait for initialData to be set before rendering the chart
  if (!initialData) {
    return (
      <div style={stateContainerStyle}>
        <Loader active />
      </div>
    )
  }

  return (
    <div
      ref={chartContainerRef}
      className={isEmbed ? `linechart linechart--embed${isCompactEmbed ? ' linechart--compact' : ''}` : 'linechart'}
      style={{ position: 'relative', height: isEmbed ? '100%' : undefined, minHeight: 0 }}
    >
      {statusIndicators && (
        <div
          style={{
            position: 'absolute',
            top: '8px',
            right: '60px',
            zIndex: 10,
            display: 'flex',
            gap: '8px',
          }}
        >
          {statusIndicators}
        </div>
      )}
      {isCompactEmbed && (
        <div className='linechart__compact-controls' role='toolbar' aria-label='Chart controls'>
          <button type='button' aria-label='Download PNG' title='Download PNG' onClick={downloadPlot}>
            {/* Inline rather than a glyph: this is the tile's only control and U+2B73
                is missing from the stock fonts on Windows and most Android builds. */}
            <svg viewBox='0 0 24 24' width='20' height='20' aria-hidden='true' focusable='false'>
              <path
                d='M12 3v10m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>
      )}
      <Plot
        ref={plotRef}
        useResizeHandler={true}
        data={initialData}
        style={{ width: '100%', height: isEmbed ? '100%' : undefined }}
        config={config}
        onRelayouting={() => {
          bumpInteraction()
          beginDrag()
        }}
        onRelayout={range => {
          const wasInteracting = isInteractingRef.current
          bumpInteraction()
          endDrag()

          if (range.dragmode) {
            setLocalDragmode(prev => (prev === range.dragmode ? prev : range.dragmode))
          }

          const xAutorange = range['xaxis.autorange'] === true
          const yAutorange = range['yaxis.autorange'] === true
          const hasXRange = range['xaxis.range[0]'] != null && range['xaxis.range[1]'] != null
          const hasYRange = range['yaxis.range[0]'] != null && range['yaxis.range[1]'] != null

          if (wasInteracting && (xAutorange || yAutorange) && !(hasXRange || hasYRange)) {
            return
          }

          if (xAutorange) {
            setLocalXaxis(buildUtcAxis(xaxis))
          }
          if (yAutorange) {
            setLocalYaxis({ ...yaxis, rangemode: 'nonnegative' })
          }

          if (hasXRange) {
            setLocalXaxis(prev => {
              const next = { ...(prev || buildUtcAxis(xaxis)) }
              next.range = [range['xaxis.range[0]'], range['xaxis.range[1]']]
              next.autorange = false
              return next
            })
          }
          if (hasYRange) {
            setLocalYaxis(prev => {
              const next = { ...(prev || yaxis), rangemode: 'nonnegative' }
              // Prevent panning to negative Y values
              const yMin = Math.max(0, range['yaxis.range[0]'])
              const yMax = range['yaxis.range[1]']
              next.range = [yMin, yMax]
              next.autorange = false
              return next
            })
          }
        }}
        layout={layout}
      />
    </div>
  )
}

Linechart.propTypes = {
  data: instanceOf(Array),
  setData: func,
  xaxis: instanceOf(Object),
  yaxis: instanceOf(Object),
  downloadFilename: string,
  uiRevisionKey: string,
  hasActiveSession: bool,
  statusIndicators: instanceOf(Object),
  variant: string,
}

export default Linechart
